import React, { useEffect, useState } from 'react';
import { Box, Typography, Grid, CircularProgress, Alert } from '@mui/material';
import { ApiService, ApiEndpoints } from '../services/ApiService';
import { useAuth } from '../context/SimpleAuthContext';
import { useCompany } from '../contexts/CompanyContext';
import WelcomeScreen from '../components/welcome/WelcomeScreen';

/**
 * Dashboard data interface
 */
interface DashboardData {
  frameworks: number;
  obligations: number;
  controls: number;
  requirements: number;
  completionRate: {
    frameworks: {
      active: number;
      draft: number;
      total: number;
    };
    requirements: {
      completed: number;
      inProgress: number;
      total: number;
    };
  };
}

/**
 * Material Dashboard Page
 * 
 * Displays overview metrics for the compliance system
 * Fetches data from the API
 */
export default function MaterialDashboard() {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showWelcome, setShowWelcome] = useState(false);
  const { user } = useAuth();
  const { currentCompany } = useCompany();

  /**
   * Check if welcome screen should be shown
   */
  useEffect(() => {
    const hideWelcomeScreen = localStorage.getItem('hideWelcomeScreen');
    if (!hideWelcomeScreen && user) {
      setShowWelcome(true);
    }
  }, [user]);

  /**
   * Fetch dashboard data from API
   */
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        console.log('[Dashboard] Fetching data for company:', currentCompany?.id, currentCompany?.name);
        
        const response = await ApiService.get<DashboardData>(ApiEndpoints.DASHBOARD);
        setDashboardData(response);
        console.log('[Dashboard] Data fetched successfully:', response);
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
        
        // Fallback to mock data for development
        const mockData = {
          frameworks: 5,
          obligations: 28,
          controls: 42,
          requirements: 156,
          completionRate: {
            frameworks: {
              active: 3,
              draft: 2,
              total: 5
            },
            requirements: {
              completed: 98,
              inProgress: 58,
              total: 156
            }
          }
        };
        console.log('[Dashboard] Using mock data for company:', currentCompany?.id);
        setDashboardData(mockData);
      } finally {
        setLoading(false);
      }
    };

    // Only fetch data if we have a current company
    if (currentCompany) {
      fetchDashboardData();
    }
  }, [currentCompany]); // Add currentCompany as dependency

  /**
   * Loading state
   */
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  /**
   * Error state
   */
  if (error && !dashboardData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Error loading dashboard: {error}
        </Alert>
      </Box>
    );
  }

  /**
   * Calculate completion percentages
   */
  const frameworkCompletionPercentage = dashboardData?.completionRate.frameworks.active 
    ? Math.round((dashboardData.completionRate.frameworks.active / dashboardData.completionRate.frameworks.total) * 100) 
    : 0;
  
  const requirementCompletionPercentage = dashboardData?.completionRate.requirements.completed 
    ? Math.round((dashboardData.completionRate.requirements.completed / dashboardData.completionRate.requirements.total) * 100) 
    : 0;

  /**
   * Handle welcome screen close
   */
  const handleWelcomeClose = () => {
    setShowWelcome(false);
  };

  /**
   * Render component
   */
  return (
    <Box className="dashboard-container">
      {/* Welcome Screen Dialog */}
      <WelcomeScreen 
        open={showWelcome} 
        onClose={handleWelcomeClose}
        userRole={user?.role || 'User'}
        userName={user?.name || user?.email || 'User'}
      />
      <Typography variant="h4" gutterBottom>
        Compliance Dashboard
      </Typography>
      
      {/* Current Company Information */}
      {currentCompany && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'primary.light', color: 'primary.contrastText', borderRadius: 1 }}>
          <Typography variant="body1">
            <strong>Current Company:</strong> {currentCompany.name}
          </Typography>
        </Box>
      )}
      
      <Grid container spacing={3}>
        {/* Summary metrics */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Summary
          </Typography>
        </Grid>
        
        {/* Framework metric */}
        <Grid item xs={12} sm={6} md={3}>
          <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
            <Typography variant="overline" color="text.secondary">
              Frameworks
            </Typography>
            <Typography variant="h4" data-testid="frameworks-stat">
              {dashboardData?.frameworks || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {frameworkCompletionPercentage}% active
            </Typography>
          </Box>
        </Grid>
        
        {/* Obligation metric */}
        <Grid item xs={12} sm={6} md={3}>
          <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
            <Typography variant="overline" color="text.secondary">
              Obligations
            </Typography>
            <Typography variant="h4" data-testid="obligations-stat">
              {dashboardData?.obligations || 0}
            </Typography>
          </Box>
        </Grid>
        
        {/* Control metric */}
        <Grid item xs={12} sm={6} md={3}>
          <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
            <Typography variant="overline" color="text.secondary">
              Controls
            </Typography>
            <Typography variant="h4" data-testid="controls-stat">
              {dashboardData?.controls || 0}
            </Typography>
          </Box>
        </Grid>
        
        {/* Requirement metric */}
        <Grid item xs={12} sm={6} md={3}>
          <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
            <Typography variant="overline" color="text.secondary">
              Requirements
            </Typography>
            <Typography variant="h4" data-testid="requirements-stat">
              {dashboardData?.requirements || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {requirementCompletionPercentage}% completed
            </Typography>
          </Box>
        </Grid>
        
        {/* Progress section */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            Completion Progress
          </Typography>
        </Grid>
        
        {/* Framework status breakdown */}
        <Grid item xs={12} md={6}>
          <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
            <Typography variant="subtitle1" gutterBottom>
              Framework Status
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Box sx={{ width: '100px', minWidth: '100px' }}>
                <Typography variant="body2">Active</Typography>
              </Box>
              <Box sx={{ flexGrow: 1, height: 10, bgcolor: '#f0f0f0', borderRadius: 5, mr: 1 }}>
                <Box 
                  sx={{ 
                    height: '100%', 
                    borderRadius: 5, 
                    width: `${frameworkCompletionPercentage}%`,
                    bgcolor: 'success.main'
                  }} 
                />
              </Box>
              <Typography variant="body2">
                {dashboardData?.completionRate.frameworks.active || 0}/{dashboardData?.completionRate.frameworks.total || 0}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ width: '100px', minWidth: '100px' }}>
                <Typography variant="body2">Draft</Typography>
              </Box>
              <Box sx={{ flexGrow: 1, height: 10, bgcolor: '#f0f0f0', borderRadius: 5, mr: 1 }}>
                <Box 
                  sx={{ 
                    height: '100%', 
                    borderRadius: 5, 
                    width: `${dashboardData?.completionRate.frameworks.draft 
                      ? Math.round((dashboardData.completionRate.frameworks.draft / dashboardData.completionRate.frameworks.total) * 100) 
                      : 0}%`,
                    bgcolor: 'warning.main'
                  }} 
                />
              </Box>
              <Typography variant="body2">
                {dashboardData?.completionRate.frameworks.draft || 0}/{dashboardData?.completionRate.frameworks.total || 0}
              </Typography>
            </Box>
          </Box>
        </Grid>
        
        {/* Requirement status breakdown */}
        <Grid item xs={12} md={6}>
          <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
            <Typography variant="subtitle1" gutterBottom>
              Requirement Status
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Box sx={{ width: '100px', minWidth: '100px' }}>
                <Typography variant="body2">Completed</Typography>
              </Box>
              <Box sx={{ flexGrow: 1, height: 10, bgcolor: '#f0f0f0', borderRadius: 5, mr: 1 }}>
                <Box 
                  sx={{ 
                    height: '100%', 
                    borderRadius: 5, 
                    width: `${requirementCompletionPercentage}%`,
                    bgcolor: 'success.main'
                  }} 
                />
              </Box>
              <Typography variant="body2">
                {dashboardData?.completionRate.requirements.completed || 0}/{dashboardData?.completionRate.requirements.total || 0}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ width: '100px', minWidth: '100px' }}>
                <Typography variant="body2">In Progress</Typography>
              </Box>
              <Box sx={{ flexGrow: 1, height: 10, bgcolor: '#f0f0f0', borderRadius: 5, mr: 1 }}>
                <Box 
                  sx={{ 
                    height: '100%', 
                    borderRadius: 5, 
                    width: `${dashboardData?.completionRate.requirements.inProgress 
                      ? Math.round((dashboardData.completionRate.requirements.inProgress / dashboardData.completionRate.requirements.total) * 100) 
                      : 0}%`,
                    bgcolor: 'info.main'
                  }} 
                />
              </Box>
              <Typography variant="body2">
                {dashboardData?.completionRate.requirements.inProgress || 0}/{dashboardData?.completionRate.requirements.total || 0}
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
